// Lib
import moment from 'moment'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { useSelector } from 'react-redux'

// UI
import { Badge, Box, Button, Flex, HStack, Heading, Tooltip } from '@chakra-ui/react'

import { Card } from '@/components/Elements'
import ApproverTable from './ApproverTable'
import ContributorTable from './ContributorTable'

import { toast } from 'react-toastify'

//API

// Helper
import { ContributorGroupType } from '@/features/contributorGroups/constants'
import { useContributorGroup } from '@/features/contributorGroups/useContributorGroup'
import { ContributorStatus } from '@/features/offers/constants'
import { getTemplate } from '@/features/offers/constants/emailTemplate'
import { offerQuotationGroupPolicyType } from '@/features/offers/constants/offerEditRights/offerQuotationEditRights'
import { selectOfferPermissions } from '@/features/offers/slices/offerDetail.slice'
import { authSelector } from '@/stores/auth/authSlice'

const ApprovalWorkflow = () => {
  // Redux
  const offerPermissions = useSelector(selectOfferPermissions)

  const { user } = useSelector(authSelector)

  const [selectedContributors, setSelectedContributors] = useState([])
  const [selectedOptionalContributors, setSelectedOptionalContributors] = useState([])
  const [selectedApprovers, setSelectedApprovers] = useState([])

  // Hook
  const { useFetchAllContributorGroups } = useContributorGroup()
  const { setValue, getValues } = useFormContext()
  const { append: addCoreContributor, remove: removeCoreContributor } = useFieldArray({ name: 'contributors' })
  const { append: addOptionalContributors, remove: removeOptionalContributor } = useFieldArray({
    name: 'optionalContributors',
  })
  const offerSubmissionDate = useWatch({ name: `offerSubmissionDate` })
  const salesResponsibleSubmitted = useWatch({ name: `salesResponsibleSubmitted` })

  const mainSubSystemType = useWatch({ name: 'subSystems.0.type' })

  const approvalRules = useWatch({ name: 'approvalRules' })
  const contributors = useWatch({ name: 'contributors' })
  const status = useWatch({ name: 'status' })
  const optionalContributors = useWatch({ name: 'optionalContributors' })

  //
  const canEditApproval = offerPermissions[offerQuotationGroupPolicyType.QUOTATION_APPROVAL]?.edit ?? false
  const canEditAddContributor = offerPermissions[offerQuotationGroupPolicyType.QUOTATION_ADD_CONTRIBUTOR]?.edit ?? false

  const canEmailApprover =
    offerPermissions[offerQuotationGroupPolicyType.QUOTATION_APPROVAL_EMAIL_APPROVER]?.edit ?? false
  const canRemoveContributorRow =
    offerPermissions[offerQuotationGroupPolicyType.QUOTATION_APPROVAL_REMOVE_CONTRIBUTOR]?.edit ?? false

  const { data: fetchContributorGroupsResponse, isFetched: isFetchedContributorGroups } = useFetchAllContributorGroups({
    page: 1,
    pageSize: 0,
    filters: { status: 'active' },
  })

  const coreGroups = useMemo(() => {
    if (!Array.isArray(fetchContributorGroupsResponse?.data)) {
      return []
    }

    return fetchContributorGroupsResponse.data.filter(
      (group) => group.requiredInOffer === 'yes' || group.requiredInOffer === true
    )
  }, [fetchContributorGroupsResponse?.data])

  const optionalGroups = useMemo(() => {
    if (!Array.isArray(fetchContributorGroupsResponse?.data)) {
      return []
    }
    return fetchContributorGroupsResponse.data.filter(
      (group) => group.requiredInOffer === 'no' || group.requiredInOffer === false
    )
  }, [fetchContributorGroupsResponse?.data])

  useEffect(() => {
    if (offerSubmissionDate && mainSubSystemType) {
      const currentDate = moment()

      const dateDiff = moment(offerSubmissionDate).diff(currentDate, 'day')
      if (dateDiff < 0) {
        return
      }

      const currentContributorDetails = getValues('contributors')
      const currentOptionalContributorDetails = getValues('optionalContributors')
      const currentApproverDetails = getValues('approvers') //Can't use the watch values, as it will become a loop

      const minLevel = Math.min(...currentApproverDetails.map((detail) => detail.level))
      const totalLevelOfApprover = 1 + (3 - minLevel + 1) // Contributor + level of approver
      const dayBetweenLevel = dateDiff / totalLevelOfApprover

      //Set the last approver level to the
      currentContributorDetails.forEach((detail, index) => {
        if (!detail.dueDate) {
          //Contributor 25% of the time

          let newDueDate = moment(offerSubmissionDate).subtract(
            Math.floor(dayBetweenLevel * totalLevelOfApprover),
            'day'
          )
          if (newDueDate.day() === 6) {
            //Sat
            newDueDate = newDueDate.add(2, 'day')
          } else if (newDueDate.day() === 0) {
            newDueDate = newDueDate.add(1, 'day')
          }

          if (newDueDate) setValue(`contributors.${index}.dueDate`, newDueDate.toDate())
        }
      })
      currentOptionalContributorDetails.forEach((detail, index) => {
        if (!detail.dueDate) {
          //Contributor 25% of the time

          let newDueDate = moment(offerSubmissionDate).subtract(
            Math.floor(dayBetweenLevel * totalLevelOfApprover),
            'day'
          )
          if (newDueDate.day() === 6) {
            //Sat
            newDueDate = newDueDate.add(2, 'day')
          } else if (newDueDate.day() === 0) {
            newDueDate = newDueDate.add(1, 'day')
          }

          if (newDueDate) setValue(`optionalContributors.${index}.dueDate`, newDueDate.toDate())
        }
      })
      currentApproverDetails.forEach((detail, index) => {
        if (!detail.dueDate) {
          //Level3 - 50%, Level 2 - 40%...
          let newDueDate = moment(offerSubmissionDate).subtract(
            Math.floor(dayBetweenLevel * (detail.level - minLevel + 1)),
            'day'
          )
          if (newDueDate.day() === 6) {
            //Sat
            newDueDate = newDueDate.add(2, 'day')
          } else if (newDueDate.day() === 0) {
            newDueDate = newDueDate.add(1, 'day')
          }

          setValue(`approvers.${index}.dueDate`, newDueDate.toDate())
        }
      })
    }
  }, [offerSubmissionDate, mainSubSystemType, getValues, setValue])

  const handleAddCoreContributor = () => {
    if (!canEditApproval) {
      return
    }
    const newContributor = {
      group: 'other',
      user: {
        _id: '',
        email: '',
      },
      dueDate: undefined,
      status: ContributorStatus.REQUIRED,
      hasEdit: false,
      canEdit: true,
      GrAttachment: [],
    }
    addCoreContributor(newContributor)
  }

  const handleAddOptionalContributor = () => {
    if (!canEditApproval) {
      return
    }
    const newContributor = {
      group: 'other',
      user: {
        _id: '',
        email: '',
      },
      dueDate: undefined,
      status: ContributorStatus.REQUIRED,
      hasEdit: false,
      canEdit: true,
      GrAttachment: [],
    }
    addOptionalContributors(newContributor)
  }

  const sendEmail = useCallback(() => {
    const userDetails = []
    const contributors = getValues('contributors') || []
    const offerId = getValues('_id') || ''
    const approvers = getValues('approvers') || []

    for (const index of selectedApprovers) {
      const approverDetail = approvers[index]

      if (approverDetail && approverDetail.user?._id) {
        const dueDate = moment(approverDetail.dueDate)

        userDetails.push({
          email: approverDetail.user.email,
          group: approverDetail.group,
          level: approverDetail.level,
          name: approverDetail.user.fullName || '',
          dueDate: dueDate.isValid() ? dueDate.format('DD-MMM-YYYY') : '',
          url: '',
          reminder: 'Please help to review the approval',
        })
      }
    }

    for (const index of selectedContributors) {
      const contributorDetail = contributors[index]
      if (
        contributorDetail &&
        contributorDetail.user &&
        contributorDetail?.user?.email &&
        contributorDetail.user.email !== ''
      ) {
        let reminder = ''
        switch (contributorDetail.group) {
          case ContributorGroupType.LEGAL:
            reminder = 'Please help to review the tender document'
            break
          case ContributorGroupType.PROCUREMENT_OFFICER:
            reminder = 'Please help to source subcontractor for the maintenance contract'
            break
          default:
            reminder = 'Please help to review the approval'
            break
        }

        const dueDate = moment(contributorDetail.dueDate)
        userDetails.push({
          email: contributorDetail.user.email,
          group: contributorDetail.group,
          level: 4,
          name: contributorDetail.user.fullName || '',
          dueDate: dueDate.isValid() ? dueDate.format('DD-MMM-YYYY') : '',
          url: '',
          reminder,
        })
      }
    }
    for (const index of selectedOptionalContributors) {
      const contributorDetail = optionalContributors[index]
      if (
        contributorDetail &&
        contributorDetail.user &&
        contributorDetail?.user?.email &&
        contributorDetail.user.email !== ''
      ) {
        let reminder = ''
        switch (contributorDetail.group) {
          case ContributorGroupType.LEGAL:
            reminder = 'Please help to review the tender document'
            break
          case ContributorGroupType.PROCUREMENT_OFFICER:
            reminder = 'Please help to source subcontractor for the maintenance contract'
            break
          default:
            reminder = 'Please help to review the approval'
            break
        }

        const dueDate = moment(contributorDetail.dueDate)
        userDetails.push({
          email: contributorDetail.user.email,
          group: contributorDetail.group,
          level: 4,
          name: contributorDetail.user.fullName || '',
          dueDate: dueDate.isValid() ? dueDate.format('DD-MMM-YYYY') : '',
          url: '',
          reminder,
        })
      }
    }

    userDetails.sort((a, b) => a.level - b.level)

    if (userDetails.length > 0) {
      const projectName = getValues('projectName') || ''
      const quotationNumber = getValues('quotationNumber') || ''
      const executiveSummary = getValues('approvalRequestExecutiveSummary') || ''
      const subject = `Review For EApproval(${quotationNumber}): ${projectName} `
      const emailTo = userDetails.map((userDetail) => userDetail.email).join(';')
      const htmlBody = getTemplate(userDetails, offerId, executiveSummary)

      window.open(
        `mailto:${encodeURIComponent(emailTo)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(htmlBody)}`
      )
    } else {
      toast.error('Missing Selected User to create email template.')
    }
  }, [getValues, optionalContributors, selectedApprovers, selectedContributors, selectedOptionalContributors])

  return (
    <Card
      title={'Approval Workflow'}
      hasBorder={false}
      rightElement={
        <HStack spacing="20px">
          {canEmailApprover && (
            <>
              <Button size="sm" colorScheme="blue" onClick={() => sendEmail()}>
                Email
              </Button>
            </>
          )}
        </HStack>
      }
    >
      <HStack>
        {salesResponsibleSubmitted && <Badge colorScheme="red">Sales Responsible Submitted</Badge>}
        <Tooltip
          label={
            !!approvalRules && Array.isArray(approvalRules)
              ? approvalRules.map((rule) => rule?.name || '').join(';')
              : ''
          }
        >
          <Badge colorScheme="green">
            Approval Rules: {!!approvalRules && Array.isArray(approvalRules) ? approvalRules.length : 0}
          </Badge>
        </Tooltip>
      </HStack>

      <Box mt="20px" overflow="auto">
        <Flex justifyContent={'space-between'} mb="3">
          <Heading size="md" mb="2">
            Core Contributor
          </Heading>
          {canEditAddContributor && (
            <Button size="sm" colorScheme="green" onClick={handleAddCoreContributor}>
              Add
            </Button>
          )}
        </Flex>
        <ContributorTable
          contributors={contributors}
          groups={coreGroups}
          removeContributor={removeCoreContributor}
          setSelectedUser={setSelectedContributors}
          canRemove={canRemoveContributorRow}
          canEditTable={canEditApproval}
        />
      </Box>
      <Box mt="20px" overflow="auto">
        <Flex justifyContent={'space-between'} mb="3">
          <Heading size="md" mb="2">
            Optional Contributor
          </Heading>
          {canEditAddContributor && (
            <Button size="sm" colorScheme="green" onClick={handleAddOptionalContributor}>
              Add
            </Button>
          )}
        </Flex>

        <ContributorTable
          contributors={optionalContributors}
          groups={optionalGroups}
          removeContributor={removeOptionalContributor}
          setSelectedUser={setSelectedOptionalContributors}
          canRemove={canRemoveContributorRow}
          canEditTable={canEditApproval}
          isOptional
        />
      </Box>

      <Box mt="20px" overflow="auto">
        <Heading size="md" mb="2">
          Quotation Approver
        </Heading>
        <ApproverTable setSelectedApprover={setSelectedApprovers} />
      </Box>
    </Card>
  )
}

export default ApprovalWorkflow
